import * as React from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
} from "@mui/icons-material";
import { formatNumberWithCommas } from "../../../../utils/generalFunctions";
import { green } from "../../../../shared-theme/themePrimitives";
import OnGeneralMessageDialog from "../../../../components/Dialogs/OnGeneralMessageDialog";
import AlertMessageDialog from "../../../../components/Dialogs/alertMessageDialog";
/**
 * AssignPrestamoDialog
 *
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - onAssign?: (selectedIds: string[]) => void
 * - portafolio_id: string
 */
export default function AssignPrestamoDialog({
  open,
  onClose,
  onAssign,
  portafolio_id,
}) {
  const [query, setQuery] = React.useState("");
  const [loans, setLoans] = React.useState([]);

  const [selected, setSelected] = React.useState([]);
  // Implement onGeneralMessageDialog: a handler to show a general message dialog (e.g., for success/error feedback)
  // We'll use a state to control the dialog and a function to trigger it.

  const [generalMessage, setGeneralMessage] = React.useState({
    open: false,
    title: "",
    message: "",
    severity: "info", // could be 'success', 'error', 'warning', 'info'
  });

  const [alertMessage, setAlertMessage] = React.useState({
    open: false,
    title: "",
    message: "",
    severity: "info",
  });

  const onGeneralMessageDialog = ({ title, message, severity = "info" }) => {
    setGeneralMessage({
      open: true,
      title,
      message,
      severity,
    });
  };

  const handleCloseGeneralMessage = () => {
    setGeneralMessage((prev) => ({ ...prev, open: false }));
    // Close the main dialog and reset selection after user acknowledges the message
    setSelected([]);
    onClose?.();
  };

  const showAlertMessage = ({ title, message, severity = "info" }) => {
    setAlertMessage({
      open: true,
      title,
      message,
      severity,
    });
  };

  const handleCloseAlertMessage = () => {
    setAlertMessage((prev) => ({ ...prev, open: false }));
  };

  React.useEffect(() => {
    if(open){
    setGeneralMessage((prev) => ({ ...prev, open: false }));
    setAlertMessage((prev) => ({ ...prev, open: false }));
    fetch(`${process.env.REACT_APP_SERVER_URL}/api/portafolios/${portafolio_id}/prestamos/disponibles/${localStorage.getItem('idcompany')}`,{
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
      },
      
    })
      .then((res) => res.json())
      .then((data) => {
        setLoans(data);
      });
    }
  }, [open]);

  const handleGuardar = () => {
    setGeneralMessage((prev) => ({ ...prev, open: false }));
    setAlertMessage((prev) => ({ ...prev, open: false }));
    fetch(`${process.env.REACT_APP_SERVER_URL}/api/portafolios/${portafolio_id}/prestamos`,{
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
      },
      body: JSON.stringify({ prestamos_id: selected, created_by: localStorage.getItem('id') }),
     })
     .then((res) => res.json())
     .then((data) => {
      showAlertMessage({
        title: "Éxito",
        message: "Préstamos asignados correctamente al portafolio",
        severity: "success",
      });
      // Close the main dialog after success with a delay to show the alert
      setTimeout(() => {
        setSelected([]);
        onClose?.();
      }, 2000); // 2 second delay
     })
     .catch((err) => {
      showAlertMessage({
        title: "Error",
        message: "Error al asignar préstamos al portafolio",
        severity: "error",
      });
     });
  }


  const filtered = loans.filter((l) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return l.id.toLowerCase().includes(q) || l.cliente_nombre.toLowerCase().includes(q);
  });

  const allVisibleChecked =
    filtered.length > 0 && filtered.every((l) => selected.includes(l.id));
  const someVisibleChecked =
    filtered.some((l) => selected.includes(l.id)) && !allVisibleChecked;

  const toggleOne = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleAllVisible = () => {
    if (allVisibleChecked) {
      setSelected((prev) => prev.filter((id) => !filtered.some((l) => l.id === id)));
    } else {
      setSelected((prev) => Array.from(new Set([...prev, ...filtered.map((l) => l.id)])));
    }
  };

  const handleAssign = () => {
    if (selected.length === 0) {
      showAlertMessage({
        title: "Advertencia",
        message: "Debe seleccionar al menos un préstamo para asignar",
        severity: "warning",
      });
      return;
    }
    
    onGeneralMessageDialog({
      title: "Confirmar Asignación",
      message: `¿Está seguro que desea asignar ${selected.length} préstamo${selected.length === 1 ? '' : 's'} al portafolio?`,
      severity: "info",
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pr: 6 }}>Asignar Préstamos al Portafolio</DialogTitle>
      <IconButton
        onClick={onClose}
        sx={{ position: "absolute", right: 8, top: 8 }}
        aria-label="cerrar"
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "stretch", sm: "center" }}>
          <TextField
            fullWidth
            placeholder="Buscar por ID de Préstamo, nombre del cliente..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button variant="outlined" startIcon={<FilterListIcon />}>Filtrar</Button>
        </Stack>

        <Box sx={{ mt: 3, height: 360, overflowY: "auto", pr: 1 }}>
          <List disablePadding>
            <ListItem
              divider
              secondaryAction={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    {someVisibleChecked ? "Algunos seleccionados" : allVisibleChecked ? "Todos seleccionados" : "Seleccionar visibles"}
                  </Typography>
                  <Checkbox
                    edge="end"
                    indeterminate={someVisibleChecked}
                    checked={allVisibleChecked}
                    onChange={toggleAllVisible}
                  />
                </Stack>
              }
            >
              <ListItemText
                primary={<Typography variant="subtitle2">Préstamos Visibles ({filtered.length})</Typography>}
                secondary={<Typography variant="caption" color="text.secondary">Marcar para seleccionar todos los préstamos filtrados actualmente</Typography>}
              />
            </ListItem>

            {filtered.map((l) => (
              <ListItem key={l.id} divider>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: "primary.light", color: "primary.main" }}>{l.cliente_nombre[0]}</Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography fontWeight={600} color="primary.main">{l.loan_id}</Typography>
                      <Typography>— {l.cliente_nombre}</Typography>
                    </Stack>
                  }
                  secondary={
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">{l.producto_financiero}</Typography>
                      <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />
                      <Typography variant="body2" color="text.secondary">{formatNumberWithCommas(l.monto_solicitado)}</Typography>
                    </Stack>
                  }
                />
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mr: 1 }}>
                  <Chip
                    label={l.estado ?? "activo"}
                    size="small"
                    color={l.estado === "activo" ? "success" : "default"}
                    variant={l.estado === "activo" ? "soft" : "outlined"}
                  />
                </Stack>
                <Checkbox
                  edge="end"
                  checked={selected.includes(l.id)}
                  onChange={() => toggleOne(l.id)}
                />
              </ListItem>
            ))}

            {filtered.length === 0 && (
              <ListItem>
                <ListItemText
                  primary={<Typography>Sin resultados</Typography>}
                  secondary={<Typography variant="body2" color="text.secondary">Intenta con un término de búsqueda diferente.</Typography>}
                />
              </ListItem>
            )}
          </List>
        </Box>
      </DialogContent>
      <DialogActions>
        <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1} sx={{ width: "100%" }} justifyContent="flex-end">
          <Button variant="outlined" onClick={onClose}>Cancelar</Button>
          <Button
            variant="contained"
            startIcon={<AssignmentTurnedInIcon />}
            onClick={handleAssign}
            disabled={selected.length === 0}
            sx={{fontWeight:"bold",minWidth:120}}
            color="primarybutton"
          >
            Asignar {selected.length || ""} {selected.length === 1 ? "Préstamo" : "Préstamos"}
          </Button>
        </Stack>
      </DialogActions>
      <OnGeneralMessageDialog 
        open={generalMessage.open} 
        onClose={handleCloseGeneralMessage} 
        title={generalMessage.title} 
        message={generalMessage.message} 
        severity={generalMessage.severity}
        buttonText="Confirmar"
        onRegister={handleGuardar}
      />
      <AlertMessageDialog
        open={alertMessage.open}
        setOpen={setAlertMessage}
        title={alertMessage.title}
        message={alertMessage.message}
        severity={alertMessage.severity}
        handleClose={handleCloseAlertMessage}
      />
    </Dialog>
  );
}

        